import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import {
    Card,
    Collapse,
    CircularProgress,
    Fade,
    Grid,
    Icon,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";
import { getUproColor } from "utils/colors";
import logo_alt from "assets/images/comedor.png";
import { formatMoneyPunto, formatMoneyPuntoDecimal } from "utils/formatters";

const UPSERT_PAGO_PROVEEDOR = gql`
  mutation createPagoProveedor($input: PagoProveedorInput!) {
    createPagoProveedor(input: $input) {
      id
      idRemito
      estado
      pagado
      fechaEstimadaPago
      fechaPago
      formaPago
      cuenta
      observacion
    }
  }
`;

const PAYMENT_ACTIONS = {
    NOW: "Pagar ahora",
    LATER: "Diferir el pago",
};

const DEFAULT_FORMA_PAGO = "Transferencia";
const EMPTY_IMPUESTOS = {
    montoIva105: null,
    montoIva21: null,
    percepcionIibb: null,
    percepcionIva: null,
    otrosTributos: null,
};

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const moneyEquals = (left, right) => Math.abs(roundMoney(left) - roundMoney(right)) <= 0.01;

const resolveIvaAmounts = (impuestos = {}, pago = {}) => {
    const alicuotaIva = Number(impuestos?.alicuotaIVA) || 0;
    const montoBase = Number(impuestos?.montoBase ?? pago?.montoBase ?? pago?.monto ?? 0) || 0;
    const montoIva = Number(impuestos?.montoIVA) || 0;
    const hasIva105 = impuestos?.montoIVA105 !== null && impuestos?.montoIVA105 !== undefined && impuestos?.montoIVA105 !== "";
    const hasIva21 = impuestos?.montoIVA21 !== null && impuestos?.montoIVA21 !== undefined && impuestos?.montoIVA21 !== "";

    // Prioriza montos individuales guardados para poder re-editar exactamente lo cargado.
    if (hasIva105 || hasIva21) {
        return {
            montoIva105: hasIva105 ? roundMoney(Number(impuestos?.montoIVA105) || 0) : null,
            montoIva21: hasIva21 ? roundMoney(Number(impuestos?.montoIVA21) || 0) : null,
        };
    }

    if (montoIva > 0 && moneyEquals(alicuotaIva, 0.315)) {
        const iva105 = roundMoney(montoIva * (0.105 / 0.315));
        return {
            montoIva105: iva105,
            montoIva21: roundMoney(montoIva - iva105),
        };
    }

    if ((montoIva > 0 || montoBase > 0) && moneyEquals(alicuotaIva, 0.105)) {
        return {
            montoIva105: roundMoney(montoIva > 0 ? montoIva : montoBase * 0.105),
            montoIva21: null,
        };
    }

    if ((montoIva > 0 || montoBase > 0) && moneyEquals(alicuotaIva, 0.21)) {
        return {
            montoIva105: null,
            montoIva21: roundMoney(montoIva > 0 ? montoIva : montoBase * 0.21),
        };
    }

    if (montoIva > 0) {
        return {
            montoIva105: null,
            montoIva21: roundMoney(montoIva),
        };
    }

    return { montoIva105: null, montoIva21: null };
};

const buildInitialForm = (pago) => {
    const formaPago = pago?.formaPago || DEFAULT_FORMA_PAGO;
    const isCheque = formaPago === "Cheque";
    const impuestos = pago?.impuestos || {};
    const ivaAmounts = resolveIvaAmounts(impuestos, pago);

    return {
        tipoPago: pago?.estado === "OPEmitida" ? PAYMENT_ACTIONS.LATER : PAYMENT_ACTIONS.NOW,
        fechaDiferido: pago?.fechaEstimadaPago ? dayjs(pago.fechaEstimadaPago).format("YYYY-MM-DD") : "",
        formaPago,
        ...(isCheque
            ? { chequeNro: pago?.chequeNro || pago?.cuenta || "" }
            : { cbu: pago?.cuenta || "" }),
        numeroFactura: pago?.numeroFactura || "",
        observaciones: pago?.observacion || "",
        ...ivaAmounts,
        percepcionIibb: impuestos?.montoPercepcionIIBB ?? null,
        percepcionIva: impuestos?.montoPercepcionIVA ?? null,
        otrosTributos: impuestos?.montoOtrosImpuestos ?? null,
    };
    ;
};

const GET_PROVEEDORES = gql`
  query getProveedores(
    $filter: JSON
    $search: String
    $pagination: PaginationInput
    $order: Order
  ) {
    paginationInfo {
      pages
      total
    }
    proveedores(filter: $filter, search: $search, pagination: $pagination, order: $order) {
      id
      cbu
      documentos {
        descripcion
        nombre
        file
        estado
      }
    }
  }
`;

const ADD_PROVEEDOR = gql`
  mutation addProveedor($input: ProveedorInput!) {
    saveProveedor(input: $input) {
      id
    }
  }
`;

export default function ModalOrdenPago({ open, handleClose, idRemito, infoPago, onSuccess }) {
    const { handleSnackbar } = useContext(MessageManager);
    const { confirmar } = useContext(ConfirmarContext);
    const [form, setForm] = useState(() => buildInitialForm(infoPago?.pagosProveedor));
    const [proveedorCBU, setProveedorCBU] = useState([]);
    const [showImpuestos, setShowImpuestos] = useState(false);

    const [upsertPagoProveedor, { loading: loadingSave }] = useMutation(UPSERT_PAGO_PROVEEDOR, {
        onError: (error) => {
            handleSnackbar(error.message, "error");
        },
    });

    const [updateProveedor, { loading: loadingSaveProveedor }] = useMutation(ADD_PROVEEDOR);

    const [getProveedor, { loading: loadingProveedor, data: proveedor, refetch }] = useLazyQuery(GET_PROVEEDORES, {
        onCompleted: (data) => {
            if (data?.proveedores?.length) {
                const prov = data.proveedores[0];
                setForm((prev) => ({
                    ...prev,
                    cbu: prev.cbu || prov.cbu || "",
                }));
                const CBUarr = prov.documentos.filter(doc => doc.descripcion === "Constancia de CBU" && doc.estado === "Aprobado") || [];
                setProveedorCBU(CBUarr[0]?.file);
            } else {
                setProveedorCBU([]);
            }
        },
        onError: (error) => {
            handleSnackbar(error.message, "error");
        },
        fetchPolicy: "network-only",
        cachePolicy: "no-cache",
    });

    useEffect(() => {
        if (infoPago && infoPago?.proveedor?.id) {
            getProveedor({
                variables: {
                    filter: { id: infoPago.proveedor.id }
                },
            });
        } else {
            setProveedorCBU([]);
        }
        if (open) {
            setForm(buildInitialForm(infoPago?.pagosProveedor));
            setShowImpuestos(false);
        } else {
            setForm(buildInitialForm(null));
            setShowImpuestos(false);
        }
    }, [open, infoPago]);

    const isDiferido = form.tipoPago === PAYMENT_ACTIONS.LATER;
    const isCheque = form.formaPago === "Cheque";
    const baseTotal = Number(infoPago?.total) || 0;
    const totalBase = useMemo(() => roundMoney(baseTotal), [baseTotal]);
    const iva105Amount = useMemo(() => roundMoney(Number(form.montoIva105) || 0), [form.montoIva105]);
    const iva21Amount = useMemo(() => roundMoney(Number(form.montoIva21) || 0), [form.montoIva21]);
    const ivaAmount = useMemo(() => roundMoney(iva105Amount + iva21Amount), [iva105Amount, iva21Amount]);
    const subtotalConIva = useMemo(() => roundMoney(totalBase + ivaAmount), [totalBase, ivaAmount]);
    const impuestosAdicionales = useMemo(() => roundMoney(
        (Number(form.percepcionIibb) || 0) + (Number(form.percepcionIva) || 0) + (Number(form.otrosTributos) || 0)
    ), [form.percepcionIibb, form.percepcionIva, form.otrosTributos]);
    const totalConImpuestos = useMemo(() => roundMoney(subtotalConIva + impuestosAdicionales), [subtotalConIva, impuestosAdicionales]);

    const handleClickDocument = (url) => {
        window.open(
            resolveStoredFileUrl(url),
            "_blank",
            "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=400,height=400"
        );
    };

    const validationErrors = useMemo(() => {
        const nextErrors = {};

        if (!form.numeroFactura?.trim()) {
            nextErrors.numeroFactura = "Ingresa el numero de factura.";
        }

        if (isDiferido && !form.fechaDiferido) {
            nextErrors.fechaDiferido = "Selecciona la fecha estimada de pago.";
        }

        if (!form.formaPago) {
            nextErrors.formaPago = "Selecciona el metodo de pago.";
        }

        if (isCheque) {
            if (!form.chequeNro?.trim()) {
                nextErrors.chequeNro = "Ingresa el numero del cheque.";
            }
        } else if (!form.cbu?.trim()) {
            nextErrors.cbu = "Ingresa la cuenta o referencia.";
        }

        return nextErrors;
    }, [form, isCheque, isDiferido]);

    const confirmDisabled = loadingSave || !idRemito || Object.keys(validationErrors).length > 0;
    const minDate = dayjs().format("YYYY-MM-DD");

    const handleModalClose = () => {
        if (loadingSave) return;
        handleClose();
    };

    const handleTipoPagoChange = (event) => {
        const { value } = event.target;
        setForm((prev) => ({
            ...prev,
            tipoPago: value,
            fechaDiferido: value === PAYMENT_ACTIONS.LATER ? prev.fechaDiferido : "",
        }));
    };

    const handleFormaPagoChange = (event) => {
        const { value } = event.target;
        setForm((prev) => ({
            ...prev,
            formaPago: value,
            chequeNro: value === "Cheque" ? prev.chequeNro : "",
            cbu: value === "Cheque" ? "" : prev.cbu,
        }));
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const hasImpuestosChanges = useMemo(() => {
        const hasIva105 = form.montoIva105 !== null && form.montoIva105 !== undefined && form.montoIva105 !== "";
        const hasIva21 = form.montoIva21 !== null && form.montoIva21 !== undefined && form.montoIva21 !== "";
        const hasIibb = form.percepcionIibb !== null && form.percepcionIibb !== undefined && form.percepcionIibb !== "";
        const hasIvaPerc = form.percepcionIva !== null && form.percepcionIva !== undefined && form.percepcionIva !== "";
        const hasOtros = form.otrosTributos !== null && form.otrosTributos !== undefined && form.otrosTributos !== "";
        return hasIva105 || hasIva21 || hasIibb || hasIvaPerc || hasOtros;
    }, [form.montoIva105, form.montoIva21, form.percepcionIibb, form.percepcionIva, form.otrosTributos]);

    const handleToggleImpuestos = () => {
        if (!showImpuestos) {
            setShowImpuestos(true);
            return;
        }

        setShowImpuestos(false);
        if (!hasImpuestosChanges) return;

        confirmar({
            title: "Deshacer impuestos",
            message: "¿Desea deshacer los cambios de impuestos y volver al total base?",
            icon: "warning",
            func: () => {
                setForm((prev) => ({
                    ...prev,
                    ...EMPTY_IMPUESTOS,
                }));
            },
        });
    };

    const handleConfirm = async () => {
        if (confirmDisabled) return;
        const montoIVA105 = roundMoney(Number(form.montoIva105) || 0);
        const montoIVA21 = roundMoney(Number(form.montoIva21) || 0);
        const montoPercepcionIIBB = roundMoney(Number(form.percepcionIibb) || 0);
        const montoPercepcionIVA = roundMoney(Number(form.percepcionIva) || 0);
        const montoOtrosImpuestos = roundMoney(Number(form.otrosTributos) || 0);
        const impuestosTotal = roundMoney(ivaAmount + montoPercepcionIIBB + montoPercepcionIVA + montoOtrosImpuestos);
        const montoBase = totalBase;
        const alicuotaIVA = montoBase > 0 ? roundMoney(ivaAmount / montoBase) : 0;

        const payload = {
            idRemito: Number(idRemito),
            estado: isDiferido ? "OPEmitida" : "OPFinalizada",
            pagado: !isDiferido,
            fechaEstimadaPago: isDiferido ? dayjs(form.fechaDiferido).format("YYYY-MM-DD HH:mm:ss") : null,
            fechaPago: !isDiferido ? dayjs().format("YYYY-MM-DD HH:mm:ss") : null,
            numeroFactura: form.numeroFactura?.trim(),
            monto: totalConImpuestos,
            montoBase,
            impuestosTotal,
            montoTotal: totalConImpuestos,
            impuestos: {
                alicuotaIVA: alicuotaIVA > 0 ? alicuotaIVA : null,
                montoBase,
                montoIVA: ivaAmount,
                montoIVA105: montoIVA105 > 0 ? montoIVA105 : null,
                montoIVA21: montoIVA21 > 0 ? montoIVA21 : null,
                montoPercepcionIIBB,
                montoPercepcionIVA,
                montoOtrosImpuestos,
            },
            formaPago: form.formaPago,
            ...(isCheque
                ? { chequeNro: form?.chequeNro?.trim() }
                : { cuenta: form?.cbu?.trim() }),
            observacion: form.observaciones?.trim() ? form.observaciones?.trim() : null,
        };

        try {
            await upsertPagoProveedor({
                variables: {
                    input: payload,
                },
            });

            handleSnackbar("Orden de pago actualizada", "success");
            setForm(buildInitialForm(null));
            if (onSuccess) {
                onSuccess();
            }
            handleClose();
        } catch (error) {
            // Error reportado en onError de useMutation
        }
    };

    const handleSave = () => {
        if (loadingSaveProveedor) return;
        if (!form.cbu?.trim()) {
            handleSnackbar("Ingresa un CBU valido para guardar en el proveedor", "warning");
            return;
        }
        if (!infoPago?.proveedor?.id) {
            handleSnackbar("No se pudo obtener el proveedor asociado para guardar el CBU", "error");
            return;
        }
        const proveedor = infoPago.proveedor;
        updateProveedor({
            variables: {
                input: {
                    id: proveedor.id || null,
                    cbu: form.cbu?.trim(),
                },
            },
        })
            .then(() => {
                handleSnackbar("CBU actualizado correctamente", "success");
                onSuccess?.();
            })
            .catch(() => {
                handleSnackbar("Error al actualizar el CBU", "error");
            });
    };

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={handleModalClose}
            closeAfterTransition
        >
            {
                loadingProveedor ? (
                    <Fade in={loadingProveedor}>
                        <Card
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                maxHeight: "90%",
                                width: { xs: "90%", sm: "45%", xxl: "35%" },
                                p: 3,
                                overflowY: "auto",
                            }}
                        >
                            <SoftBox display="flex" justifyContent="center" alignItems="center">
                                <SoftBox
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    flexDirection="column"
                                    height="100%"
                                >
                                    {logo_alt && (
                                        <SoftBox component="img" src={logo_alt} alt="UPrO Logo" width="15rem" p={3} />
                                    )}
                                    <SoftTypography variant="h4" align="center" color="dark">
                                        Cargando datos
                                    </SoftTypography>
                                    <SoftBox
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        flexDirection="column"
                                        gap={2}
                                    >
                                        <SoftTypography variant="h5" align="center" color="dark">
                                            Por favor espera
                                        </SoftTypography>
                                        <CircularProgress size={30} color="primary" />
                                    </SoftBox>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Fade>
                ) : (
                    <Fade in={open}>
                        <Card
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                maxHeight: "90%",
                                width: "auto",
                                p: 3,
                                overflowY: "auto",
                            }}
                        >
                            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                                <SoftBox display="flex" alignItems="center" gap={1}>
                                    <Icon fontSize="md" sx={{ color: getUproColor("uproGreen") }}>
                                        attach_money
                                    </Icon>
                                    <SoftTypography variant="h6" fontWeight="bold">
                                        Orden de Pago
                                    </SoftTypography>
                                </SoftBox>
                                <Tooltip title="Cerrar" placement="top">
                                    <span>
                                        <Icon
                                            fontSize="medium"
                                            onClick={handleModalClose}
                                            sx={{
                                                cursor: loadingSave ? "not-allowed" : "pointer",
                                                opacity: loadingSave ? 0.6 : 1,
                                            }}
                                        >
                                            close
                                        </Icon>
                                    </span>
                                </Tooltip>
                            </SoftBox>

                            <SoftBox mt={3} display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                                <Card sx={{ mt: 1, pb: 2 }} style={{ flexGrow: 1, minWidth: 300, height: "max-content" }}>
                                    <Grid container p={2} spacing={1}>
                                        <Grid item xs={12}>
                                            <SoftBox display="flex" alignItems="center" gap={1}>
                                                <Icon>wysiwyg</Icon>
                                                <SoftTypography variant="h6">General</SoftTypography>
                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <SoftBox display="flex" gap={2} justifyContent="space-between" alignItems="center" flexDirection="column">
                                                <SoftBox width={{ xs: "100%" }}>
                                                    <InputLabel htmlFor="tipoPago">
                                                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                            Tipo de pago
                                                        </SoftTypography>
                                                    </InputLabel>
                                                    <Select
                                                        id="tipoPago"
                                                        name="tipoPago"
                                                        value={form.tipoPago}
                                                        onChange={handleTipoPagoChange}
                                                        size="small"
                                                        fullWidth
                                                        disabled={loadingSave}
                                                    >
                                                        <MenuItem value={PAYMENT_ACTIONS.NOW}>{PAYMENT_ACTIONS.NOW}</MenuItem>
                                                        <MenuItem value={PAYMENT_ACTIONS.LATER}>{PAYMENT_ACTIONS.LATER}</MenuItem>
                                                    </Select>
                                                </SoftBox>
                                                {isDiferido && (
                                                    <SoftBox width={{ xs: "100%" }}>
                                                        <InputLabel htmlFor="fechaDiferido">
                                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                                Fecha estimada del pago
                                                            </SoftTypography>
                                                        </InputLabel>
                                                        <Tooltip
                                                            title={validationErrors.fechaDiferido || ""}
                                                            placement="right"
                                                            disableHoverListener={!validationErrors.fechaDiferido}
                                                        >
                                                            <SoftInput
                                                                type="date"
                                                                id="fechaDiferido"
                                                                name="fechaDiferido"
                                                                value={form.fechaDiferido}
                                                                onChange={handleInputChange}
                                                                fullWidth
                                                                error={Boolean(validationErrors.fechaDiferido)}
                                                                disabled={loadingSave}
                                                                inputProps={{ min: minDate }}
                                                            />
                                                        </Tooltip>
                                                    </SoftBox>
                                                )}

                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <SoftBox width={{ xs: "100%" }}>
                                                <InputLabel htmlFor="numeroFactura">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        N° de Factura
                                                    </SoftTypography>
                                                </InputLabel>
                                                <SoftInput
                                                    id="numeroFactura"
                                                    name="numeroFactura"
                                                    value={form.numeroFactura}
                                                    onChange={handleInputChange}
                                                    fullWidth
                                                    error={Boolean(validationErrors.numeroFactura)}
                                                    disabled={loadingSave}
                                                />
                                            </SoftBox>
                                        </Grid>
                                    </Grid>
                                </Card>

                                <SoftBox display="flex" flexDirection="column" flexGrow={1} minWidth={{ xs: "100%", sm: 300 }}>
                                    <Card sx={{ mt: 1, position: "relative", zIndex: 3 }}>
                                        <Grid container p={2} spacing={1}>
                                            <Grid item xs={12}>
                                                <SoftBox display="flex" alignItems="center" gap={1}>
                                                    <Icon>payment</Icon>
                                                    <SoftTypography variant="h6">Forma de Pago</SoftTypography>
                                                </SoftBox>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <InputLabel htmlFor="formaPago">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Método
                                                    </SoftTypography>
                                                </InputLabel>
                                                <Tooltip
                                                    title={validationErrors.formaPago || ""}
                                                    placement="right"
                                                    disableHoverListener={!validationErrors.formaPago}
                                                >
                                                    <Select
                                                        id="formaPago"
                                                        name="formaPago"
                                                        value={form.formaPago}
                                                        onChange={handleFormaPagoChange}
                                                        size="small"
                                                        sx={{ minWidth: 160 }}
                                                        disabled={loadingSave}
                                                        error={Boolean(validationErrors.formaPago)}
                                                    >
                                                        <MenuItem value="Transferencia">Transferencia</MenuItem>
                                                        <MenuItem value="Cheque">Cheque</MenuItem>
                                                    </Select>
                                                </Tooltip>
                                            </Grid>

                                            {isCheque ? (
                                                <Grid item xs={12}>
                                                    <InputLabel htmlFor="chequeNro">
                                                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                            Cheque Nro
                                                        </SoftTypography>
                                                    </InputLabel>
                                                    <Tooltip
                                                        title={validationErrors.chequeNro || ""}
                                                        placement="right"
                                                        disableHoverListener={!validationErrors.chequeNro}
                                                    >
                                                        <SoftInput
                                                            id="chequeNro"
                                                            name="chequeNro"
                                                            value={form.chequeNro}
                                                            onChange={handleInputChange}
                                                            disabled={loadingSave}
                                                            error={Boolean(validationErrors.chequeNro)}
                                                        />
                                                    </Tooltip>
                                                </Grid>
                                            ) : (
                                                <Grid item xs={12}>
                                                    <InputLabel htmlFor="cbu">
                                                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                            CBU {form.formaPago === "Transferencia" ? "*" : ""}
                                                        </SoftTypography>
                                                    </InputLabel>

                                                    <SoftBox display="flex" gap={1} alignItems="center">
                                                        <Tooltip
                                                            title={validationErrors.cbu || ""}
                                                            placement="top"
                                                            disableHoverListener={!validationErrors.cbu}
                                                        >
                                                            <SoftInput
                                                                id="cbu"
                                                                name="cbu"
                                                                value={form.cbu}
                                                                onChange={handleInputChange}
                                                                disabled={loadingSave}
                                                                error={Boolean(validationErrors.cbu)}
                                                            />
                                                        </Tooltip>
                                                        {/* Save to proveedor (Cuenta) and view provider CBU document */}
                                                        <SoftBox display="flex" gap={1} alignItems="center">
                                                            <Tooltip title={false ? 'Guardando...' : 'Guardar cuenta en proveedor'}>
                                                                <SoftButton
                                                                    color={false ? 'info' : 'uproGreen'}
                                                                    circular
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={handleSave}
                                                                    sx={{ minWidth: 40, width: 40, height: 40, padding: 0 }}
                                                                    disabled={Boolean(validationErrors.cbu)}
                                                                >
                                                                    {false ? <CircularProgress size={18} color="inherit" /> : <Icon>save</Icon>}
                                                                </SoftButton>
                                                            </Tooltip>
                                                            <Tooltip title="Ver Constancia de CBU del proveedor">
                                                                <SoftButton
                                                                    color="info"
                                                                    circular
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => proveedorCBU ? handleClickDocument(proveedorCBU) : handleSnackbar("No se pudo obtener el CBU del proveedor", "warning")}
                                                                    sx={{ minWidth: 40, width: 40, height: 40, padding: 0 }}
                                                                    disabled={!proveedorCBU}
                                                                >
                                                                    <Icon>visibility</Icon>
                                                                </SoftButton>
                                                            </Tooltip>
                                                        </SoftBox>
                                                    </SoftBox>
                                                </Grid>
                                            )}

                                            <Grid item xs={12}>
                                                <InputLabel htmlFor="observaciones">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Observaciones
                                                    </SoftTypography>
                                                </InputLabel>
                                                <SoftInput
                                                    id="observaciones"
                                                    name="observaciones"
                                                    value={form.observaciones}
                                                    onChange={handleInputChange}
                                                    multiline
                                                    minRows={3}
                                                    fullWidth
                                                    disabled={loadingSave}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Card>
                                    <SoftBox
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{ mt: -1.5, mb: -1.5, position: "relative", zIndex: 4 }}
                                    >
                                        <SoftButton
                                            variant="contained"
                                            size="small"
                                            onClick={handleToggleImpuestos}
                                            disableRipple
                                            disableFocusRipple
                                            sx={{
                                                borderColor: "#3a3a3a",
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 18, mr: 0.5 }}>
                                                {showImpuestos ? "remove" : "add"}
                                            </Icon>
                                            {showImpuestos ? "Ocultar seccion impuestos" : "Ver seccion impuestos"}
                                        </SoftButton>
                                    </SoftBox>

                                    <Card
                                        sx={{
                                            mt: -4,
                                            zIndex: 2,
                                            position: "relative",
                                            borderBottomLeftRadius: "0.75rem",
                                            borderBottomRightRadius: "0.75rem",
                                        }}
                                    >
                                        <SoftBox
                                            bgColor={getUproColor("uproPink")}
                                            pt={4}
                                            position="relative"
                                            sx={{
                                                borderBottomLeftRadius: "0.75rem",
                                                borderBottomRightRadius: "0.75rem",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Collapse in={showImpuestos}>
                                                <SoftBox px={2} pb={1}>
                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        py={1}
                                                        borderBottom="1px solid rgba(255,255,255,0.35)"
                                                    >
                                                        <SoftTypography variant="h6" fontWeight="bold" color="white" py={1}>
                                                            Importe Neto Gravado
                                                        </SoftTypography>
                                                        <SoftBox display="flex" alignItems="center" gap={1.5}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                $
                                                            </SoftTypography>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                {formatMoneyPunto(totalBase)}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        py={1}
                                                        borderBottom="1px solid rgba(255,255,255,0.35)"
                                                    >
                                                        <SoftTypography variant="button" fontWeight="bold" color="white">
                                                            IVA 10,5%
                                                        </SoftTypography>
                                                        <SoftBox width={{ xs: "48%", sm: 190 }} display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                $
                                                            </SoftTypography>
                                                            <SoftInputNumber
                                                                id="montoIva105"
                                                                name="montoIva105"
                                                                value={form.montoIva105}
                                                                onChange={handleInputChange}
                                                                disabled={loadingSave}
                                                                fullWidth
                                                            />
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        py={1}
                                                        borderBottom="1px solid rgba(255,255,255,0.35)"
                                                    >
                                                        <SoftTypography variant="button" fontWeight="bold" color="white">
                                                            IVA 21%
                                                        </SoftTypography>
                                                        <SoftBox width={{ xs: "48%", sm: 190 }} display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                $
                                                            </SoftTypography>
                                                            <SoftInputNumber
                                                                id="montoIva21"
                                                                name="montoIva21"
                                                                value={form.montoIva21}
                                                                onChange={handleInputChange}
                                                                disabled={loadingSave}
                                                                fullWidth
                                                            />
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        py={1}
                                                        borderBottom="1px solid rgba(255,255,255,0.35)"
                                                    >
                                                        <SoftBox minWidth={0} flex={1} display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                Total IVA
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox display="flex" alignItems="center" gap={1.5}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                $
                                                            </SoftTypography>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                {formatMoneyPunto(ivaAmount)}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        py={1}
                                                        borderBottom="1px solid rgba(255,255,255,0.35)"
                                                    >
                                                        <SoftTypography variant="button" fontWeight="bold" color="white">
                                                            Percepcion IIBB
                                                        </SoftTypography>
                                                        <SoftBox width={{ xs: "48%", sm: 190 }} display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                $
                                                            </SoftTypography>
                                                            <SoftInputNumber
                                                                id="percepcionIibb"
                                                                name="percepcionIibb"
                                                                value={form.percepcionIibb}
                                                                onChange={handleInputChange}
                                                                disabled={loadingSave}
                                                                fullWidth
                                                            />
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        py={1}
                                                        borderBottom="1px solid rgba(255,255,255,0.35)"
                                                    >
                                                        <SoftTypography variant="button" fontWeight="bold" color="white">
                                                            Percepcion IVA
                                                        </SoftTypography>
                                                        <SoftBox width={{ xs: "48%", sm: 190 }} display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                $
                                                            </SoftTypography>
                                                            <SoftInputNumber
                                                                id="percepcionIva"
                                                                name="percepcionIva"
                                                                value={form.percepcionIva}
                                                                onChange={handleInputChange}
                                                                disabled={loadingSave}
                                                                fullWidth
                                                            />
                                                        </SoftBox>
                                                    </SoftBox>

                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        py={1}
                                                    >
                                                        <SoftTypography variant="button" fontWeight="bold" color="white">
                                                            Importe Otros tributos
                                                        </SoftTypography>
                                                        <SoftBox width={{ xs: "48%", sm: 190 }} display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography variant="button" fontWeight="bold" color="white">
                                                                $
                                                            </SoftTypography>
                                                            <SoftInputNumber
                                                                id="otrosTributos"
                                                                name="otrosTributos"
                                                                value={form.otrosTributos}
                                                                onChange={handleInputChange}
                                                                disabled={loadingSave}
                                                                fullWidth
                                                            />
                                                        </SoftBox>
                                                    </SoftBox>
                                                </SoftBox>
                                            </Collapse>

                                            <SoftBox
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                p={2}
                                            >
                                                <SoftTypography variant="h6" fontWeight="bold" color="white">
                                                    Importe Total
                                                </SoftTypography>
                                                <SoftTypography
                                                    variant="h5"
                                                    fontWeight="bold"
                                                    color="white"
                                                >
                                                    ${formatMoneyPuntoDecimal(totalConImpuestos)}
                                                </SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                    </Card>
                                </SoftBox>

                                {!idRemito && (
                                    <SoftBox mt={1}>
                                        <SoftTypography variant="caption" color="error">
                                            No se encontro el remito asociado para actualizar el pago.
                                        </SoftTypography>
                                    </SoftBox>
                                )}
                            </SoftBox>

                            <SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                                <SoftButton
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleModalClose}
                                    circular
                                    disabled={loadingSave}
                                >
                                    <Icon sx={{ fontSize: 18, mr: 1 }}>close</Icon>
                                    Cancelar
                                </SoftButton>
                                <SoftButton
                                    color="uproGreen"
                                    onClick={handleConfirm}
                                    circular
                                    disabled={confirmDisabled}
                                >
                                    Confirmar&nbsp;
                                    {loadingSave ? <CircularProgress size={16} color="inherit" /> : <Icon>done</Icon>}
                                </SoftButton>
                            </SoftBox>
                        </Card>
                    </Fade>
                )
            }
        </Modal>
    );
}

ModalOrdenPago.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    idRemito: PropTypes.number,
    infoPago: PropTypes.shape({
        estado: PropTypes.string,
        fechaEstimadaPago: PropTypes.string,
        formaPago: PropTypes.string,
        cuenta: PropTypes.string,
        observacion: PropTypes.string,
    }),
    onSuccess: PropTypes.func,
};

ModalOrdenPago.defaultProps = {
    idRemito: null,
    infoPago: null,
    onSuccess: undefined,
};

